/**
 * Domain types for the operator console.
 *
 * Shapes follow the TDD's data model (§9) and console API (§11.2). Field names are the
 * table attribute names, so a row from the API lambda maps onto these without translation.
 * Where the TDD leaves a response shape open (§11.2 names endpoints and bodies, not every
 * response), the choice made here is marked "console contract" and must be matched by the
 * API lambda when it is built.
 */

export const SCOPES = ['us-prod', 'us-uat', 'eu-prod', 'eu-uat'] as const;
export type Scope = (typeof SCOPES)[number];

export const HEALTHS = ['ok', 'impaired', 'degraded', 'failed', 'maintenance'] as const;
export type Health = (typeof HEALTHS)[number];

export type CheckOutcome = 'PASS' | 'FAIL' | 'NOT_RUN';
export type RunOutcome = 'PASS' | 'FAIL';

export const PROPOSAL_STATUSES = [
  'open',
  'approved',
  'rejected',
  'superseded',
  'withdrawn',
  'published',
  'publish_failed',
] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

export type IncidentAction = 'none' | 'open' | 'attach' | 'auto';
export type WithdrawnReason = 'recovery' | 'page_already_says_it' | 'config_change';
export type PublishOutcome = 'success' | 'retryable' | 'uncertain' | 'conflict';
export type PublishCommand = 'report' | 'declare' | 'clear';
export type QuarantineReason =
  | 'malformed'
  | 'unknown_capability'
  | 'source_mismatch'
  | 'check_set_mismatch'
  | 'future_timestamp'
  | 'id_reused';

/** A ladder maps a fail streak (as a string key, e.g. "3") to a Health. Replaced whole (§8.4). */
export type Ladder = Record<string, Health>;

export interface Check {
  name: string;
  outcome: CheckOutcome;
  observed_at: string;
  duration_ms?: number;
  error_code?: string;
  error_message?: string;
  artifact_url?: string;
}

/** A stored run: facts only, exactly what the runner sent plus received_at (§9.2 runs). */
export interface Run {
  component_key: string;
  run_key: string;
  source: string;
  run_id: string;
  started_at: string;
  completed_at: string;
  received_at: string;
  checks: Check[];
}

/** The judgement the shared function makes of a run (§4.5): PASS or FAIL plus the driving check. */
export interface Judgement {
  outcome: RunOutcome;
  driving_check: string | null;
}

/** Console contract: run history returns each run judged by the shared function (§5.3). */
export interface JudgedRun extends Run {
  judged: Judgement;
}

export interface BlindWindow {
  component_key: string;
  opened_at: string;
  last_observed_at: string | null;
  last_run_key: string | null;
  closed_at: string | null;
  missed_runs_at_close: number | null;
}

export interface QuarantineEntry {
  quarantine_id: string;
  source: string;
  received_at: string;
  claimed_scope?: string;
  claimed_capability_id?: string;
  http_status: 400 | 409 | 422;
  reason: QuarantineReason;
  detail: string;
  raw_payload?: string;
  raw_payload_truncated?: boolean;
}

/** Console contract for GET …/runs: a page of judged runs plus the gaps and refusals in its time span. */
export interface RunHistoryPage {
  runs: JudgedRun[];
  blind_windows: BlindWindow[];
  quarantine: QuarantineEntry[];
  /** run_key to pass as `before` for the next page; null when this is the last page. */
  next_before: string | null;
}

export interface ComponentConfig {
  scope: Scope;
  capability_id: string;
  display_name: string;
  source: string;
  checks: string[];
  checks_applied_at: string;
  ladder: Ladder;
  recovery_threshold_runs: number;
  auto_recovery: boolean;
  recovery_approval_alert_min: number;
  runner_cadence_minutes: number;
  staleness_tolerance_cycles: number;
  applied_at: string;
  version: number;
  updated_by: string;
  updated_at: string;
}

/** The per-scope `_scope` item: the only monitor-written prose on the page (§8.2, §8.4). */
export interface ScopeConfig {
  scope: Scope;
  capability_id: '_scope';
  partial_recovery_template: string;
  resolution_template: string;
  version: number;
  updated_by: string;
  updated_at: string;
}

export interface ConfigHistoryEntry {
  component_key: string;
  version: string;
  changed_at: string;
  changed_by: string;
  reason: string;
  before: ComponentConfig | ScopeConfig | null;
  after: ComponentConfig | ScopeConfig;
}

export interface ComponentState {
  scope: Scope;
  capability_id: string;
  evaluated_through: string | null;
  evaluated_at: string | null;
  observed_outcome: RunOutcome | null;
  driving_check: string | null;
  fail_streak: number;
  pass_streak: number;
  last_observed_at: string | null;
  implied_status: Health;
  blind_since: string | null;
  open_proposal_id: string | null;
  published_status: Health;
  publish_uncertain: boolean;
}

export interface EvidenceRun {
  run_key: string;
  outcome: RunOutcome;
  driving_check: string | null;
  completed_at: string;
}

export interface Proposal {
  proposal_id: string;
  scope: Scope;
  component_key: string;
  capability_id: string;
  review_queue?: Scope;
  created_at: string;
  status: ProposalStatus;
  version: number;
  created_by: string;
  from_status: Health;
  to_status: Health;
  reason: string;
  evidence: EvidenceRun[];
  had_gap: boolean;
  auto_approved: boolean;
  approved_by?: string;
  approved_at?: string;
  rejected_by?: string;
  rejected_at?: string;
  rejection_reason?: string;
  incident_action?: IncidentAction;
  incident_title?: string;
  incident_body?: string;
  resolution_body?: string;
  note?: string;
  supersedes?: string;
  superseded_by?: string;
  withdrawn_reason?: WithdrawnReason;
  last_error?: string;
  published_at?: string;
  published_link?: string;
}

export interface PublishAttempt {
  proposal_id: string;
  attempt_seq: string;
  attempted_at: string;
  requested_by: string;
  outcome: PublishOutcome;
  action_taken?: PublishCommand;
  http_status: number | null;
  error?: string;
  incident_link?: string;
}

/** Console contract for GET /scopes/{scope}/components: state joined with config and the proposal in the slot. */
export interface ComponentView {
  state: ComponentState;
  config: ComponentConfig;
  proposal: Proposal | null;
}

export interface ComponentsResponse {
  scope: Scope;
  components: ComponentView[];
  /** When the API lambda produced the view; the console shows it as "as of". */
  as_of: string;
}

/** Console contract for GET /proposals/{id}. */
export interface ProposalDetail {
  proposal: Proposal;
  attempts: PublishAttempt[];
  /** Evidence runs that still exist in `runs` (they expire after 90 days). */
  evidence_runs: JudgedRun[];
  display_name: string;
}

/** Statuspage's public incidents/unresolved.json, reduced to what the console shows (§5.3). */
export type IncidentStage = 'investigating' | 'identified' | 'monitoring' | 'resolved';
export interface IncidentComponent {
  id: string;
  name: string;
  status: string;
}
export interface IncidentUpdate {
  id: string;
  status: IncidentStage;
  body: string;
  created_at: string;
}
export interface Incident {
  id: string;
  name: string;
  status: IncidentStage;
  impact: 'none' | 'minor' | 'major' | 'critical';
  shortlink: string;
  created_at: string;
  updated_at: string;
  components: IncidentComponent[];
  incident_updates: IncidentUpdate[];
}

export interface IncidentsResponse {
  scope: Scope;
  page_url: string;
  incidents: Incident[];
  /** When the public JSON was fetched; it can be about 30 seconds stale (§5.3). */
  fetched_at: string;
}

/* ---- Request bodies (§11.2) ---- */

export interface ApproveBody {
  version: number;
  approver: string;
  incident_action: Exclude<IncidentAction, 'auto'>;
  incident_title?: string;
  incident_body?: string;
  resolution_body?: string;
  include?: { id: string; version: number }[];
}

export interface RejectBody {
  version: number;
  approver: string;
  reason: string;
}

export interface RedecideBody {
  version: number;
  approver: string;
  incident_action: Exclude<IncidentAction, 'auto'>;
  incident_title?: string;
  incident_body?: string;
  resolution_body?: string;
}

export interface RetryBody {
  version: number;
  approver: string;
}

export interface ManualProposalBody {
  scope: Scope;
  capability_id: string;
  to_status: Health;
  incident_action: Exclude<IncidentAction, 'auto'>;
  incident_title?: string;
  incident_body?: string;
  approver: string;
  note: string;
}

export type ComponentConfigChanges = Partial<
  Pick<
    ComponentConfig,
    | 'display_name'
    | 'source'
    | 'checks'
    | 'ladder'
    | 'recovery_threshold_runs'
    | 'auto_recovery'
    | 'recovery_approval_alert_min'
    | 'runner_cadence_minutes'
    | 'staleness_tolerance_cycles'
  >
>;
export type ScopeConfigChanges = Partial<
  Pick<ScopeConfig, 'partial_recovery_template' | 'resolution_template'>
>;

export interface ConfigSaveBody {
  version: number;
  changes: ComponentConfigChanges | ScopeConfigChanges;
  changed_by: string;
  reason: string;
}

export interface AddComponentBody {
  capability_id: string;
  display_name: string;
  source: string;
  checks: string[];
  changed_by: string;
  reason: string;
}

export interface RevertBody {
  to_version: number;
  changed_by: string;
  reason: string;
}

/* ---- Action responses (console contract) ---- */

/** Actions that publish wait for the result and return the proposal as it now stands (§5.2). */
export interface PublishResult {
  proposal: Proposal;
  attempts: PublishAttempt[];
}

export interface Violation {
  field: string;
  message: string;
}

/** Bodies of the error responses the console must understand (§11.2 Errors). */
export interface ConflictBody<T = Proposal | ComponentConfig | ScopeConfig> {
  error: 'version_conflict';
  current: T;
}
export interface UnprocessableBody {
  error: 'not_allowed' | 'validation_failed';
  detail: string;
  violations?: Violation[];
}
export interface PublishFailedBody {
  error: 'publish_failed';
  proposal: Proposal;
  attempts: PublishAttempt[];
  last_error: string;
}

export function componentKey(scope: Scope, capabilityId: string): string {
  return `${scope}#${capabilityId}`;
}

export function splitComponentKey(key: string): { scope: Scope; capability_id: string } {
  const [scope, capability_id = ''] = key.split('#') as [Scope, string];
  return { scope, capability_id };
}

export function isScope(value: string | undefined): value is Scope {
  return (SCOPES as readonly string[]).includes(value ?? '');
}
